import {
  Badge,
  DataTable,
  Eyebrow,
  formatUtcDate,
  StatusDot,
  token,
  type DataTableColumn,
} from '@bp-files/ui';
import { CARD_SURFACE_CLASS } from '@/components/CardGrid';
import { ExportButton } from '@/components/ExportButton/ExportButton';
import { STORAGE_COPY } from '@/components/Storage/storage.copy';
import { UsageSeries } from '@/components/Storage/UsageSeries';
import { messageFrom } from '@bp-files/client';
import { DECIMAL, formatBytes } from '@/lib/formatBytes';
import { useStorageSummary } from '@/lib/useStorageSummary';
import { useStorageUsage } from '@/lib/useStorageUsage';
import { postManifestExport, type StorageSpaceRow, type StorageSummary } from '@/lib/api';

/**
 * The Storage surface — allowance against usage (SPEC §10).
 *
 * **Not a cost estimate, and STO-4 is why that is worth stating.** The earlier
 * design headlined an estimated monthly figure derived from a single global
 * per-GB rate. §17 replaced that rate with plan attributes, and an agency inside
 * its allowance owes nothing marginal — so the number it needs is how much room
 * is left, not a multiplication it cannot check. Overage, where it occurs, is
 * shown as overage.
 *
 * **It is a console, not a report, and that is a hierarchy claim.** Every figure
 * here used to render at the same weight — `text-sm` values under `text-xs`
 * labels under identical uppercase eyebrows — so an admin had to read the whole
 * page to learn which part of it needed them. Three facts now carry the screen:
 * how full storage is, whether anything is near a limit, and how much of what is
 * consumed could be reclaimed. Everything else supports those.
 *
 * Six things this encodes rather than decorates:
 *
 * - **Storage never refuses and transfer sometimes does.** PLN-11's asymmetry is
 *   the most important thing on the screen: an over-allowance storage figure is
 *   a billing conversation, while an over-cap rolling window is a colleague
 *   getting a `429`. Storage carries no "enforcing" marker anywhere, and its
 *   attention copy says uploads still succeed.
 * - **Two of the three transfer windows bite, and the server says which.** The
 *   annual one is the commercial figure whose overage is invoiced (STO-1,
 *   PLN-10). `enforcing` comes off the wire rather than being decided here —
 *   "which windows bite" is a rule, and a rule in two places drifts.
 * - **PLN-12's stage is rendered, because "over" and "being refused" are two
 *   different sentences** (STO-6). An admin who first learns of a cap from a
 *   throttled colleague has been told too late.
 * - **The bin is reclaimable and retention is not**, which is why they are no
 *   longer one list called "Also held" (STO-3). The bin is a tile in the summary
 *   because emptying it is an act; retention has its own section because nothing
 *   can be done about it until the term ends (RET-8).
 * - **Contract bytes are decimal.** Everything measured against an allowance
 *   goes through `formatBytes(…, DECIMAL)`, because the API states those figures
 *   in 10^9 (PLN-2, PLN-4). Rendering a binary "23.3 GB" beside a decimal
 *   "25 GB" is a silent 7% at GB and 10% at TB.
 * - **The series degrades on its own.** It is supplementary evidence for a band
 *   review (STO-7, PLN-5); its failure must not blank the figures an admin came
 *   for, which is why it has its own hook.
 */

function bytes(value: number): string {
  return formatBytes(value, STORAGE_COPY.units, DECIMAL);
}

/** Shared, so a bar's fill and its printed percent can never disagree. */
function percentOf(fraction: number): number {
  return Math.round(Math.min(Math.max(fraction, 0), 1) * 100);
}

/**
 * **The one arithmetic rule on this surface.** A binned file occupies the same
 * paid bytes until the purge takes it (UPL-10), so the allowance is measured
 * against active *plus* binned. Retention is a **subset** of what that already
 * counts and is never added to it (STO-3 as amended, ADR-0023) — which is the
 * edit a later reader makes, and the reason this is a named function rather than
 * an expression inlined at each of its three call sites.
 */
function heldBytes(summary: StorageSummary): number {
  return summary.storage.bytes + summary.holdings.recycleBin.bytes;
}

export function StorageSurface() {
  const summary = useStorageSummary();
  const usage = useStorageUsage();

  // The heading block is `SurfaceHeader`'s, supplied by `App.tsx` from
  // `SURFACE_COPY` — the same wrapper Files and Audit use, so a surface title
  // has one home rather than two.
  return (
    <div className="flex flex-col gap-6">
      {summary.state.kind === 'loading' ? (
        <p role="status" className="text-sm" style={{ color: token('bp-text-muted') }}>
          {STORAGE_COPY.states.loading}
        </p>
      ) : null}

      {summary.state.kind === 'error' ? (
        <SummaryError
          status={summary.state.status}
          error={summary.state.error}
          onRetry={summary.retry}
        />
      ) : null}

      {summary.state.kind === 'loaded' ? (
        <>
          <TopBar summary={summary.state.summary} />
          <NeedsAttention summary={summary.state.summary} />
          <Capacity summary={summary.state.summary} />
          <RetainedStorage holdings={summary.state.summary.holdings} />
          <TransferAllowance summary={summary.state.summary} />
          <Breakdown spaces={summary.state.summary.spaces} />
          <UsageSeries state={usage.state} />
        </>
      ) : null}
    </div>
  );
}

/**
 * What the agency bought, and the one act this surface offers.
 *
 * **EXIT-5's manifest moved here from the foot of the page**, where it sat under
 * an eyebrow and a paragraph arguing for itself. A transferred storage account is
 * GUID-named blobs in GUID-named containers (SYS-3), which is what that paragraph
 * explained — but a control an admin has to scroll past six sections to find is
 * one they never learn exists, and the argument was doing the work the position
 * should have.
 *
 * **It stays inside the loaded branch rather than moving to `SurfaceHeader`'s
 * `actions` slot**, and the distinction is not cosmetic. That slot lives in
 * `App.tsx`, above this component's own state, so a button in it renders through
 * every branch — including the 403 that is *permanent* for a plan-less agency
 * (STO-5), whose manifest would be empty (CON-13). Offering an export beside
 * "your agency has no plan" is noise. The Audit surface's sibling can sit in the
 * header precisely because its only 403 is a non-admin, who never reaches the
 * route at all.
 *
 * Neutral styling, not the blue glow: the design system spends that on one
 * primary act per surface, and a dashboard's primary act is reading it.
 */
function TopBar({ summary }: { summary: StorageSummary }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Eyebrow>{STORAGE_COPY.plan.label}</Eyebrow>
        <span className="text-sm text-body">
          {summary.plan === 'DocsEvidence'
            ? STORAGE_COPY.plan.docsEvidence
            : STORAGE_COPY.plan.docs}
        </span>
        <Badge tone="neutral">
          {summary.band === null
            ? STORAGE_COPY.plan.unbanded
            : STORAGE_COPY.plan.band(summary.band)}
        </Badge>
      </div>

      <ExportButton
        kind="manifest"
        label={STORAGE_COPY.exportManifest.action}
        request={postManifestExport}
      />
    </div>
  );
}

/** One line of the callout: the fact, and whatever a reader needs to act on it. */
interface AttentionItem {
  id: string;
  text: string;
  details: string[];
}

const WINDOW_ORDER = ['rolling7d', 'rolling30d', 'annual'] as const;

/**
 * Everything on this page that might need somebody today, gathered into one
 * place at the top.
 *
 * Built here rather than rendered inline beside each figure, because the old
 * surface did the latter: a warning line under the storage meter, another under
 * each of three transfer meters, and a fourth block for PLN-6's refusals — four
 * places to notice, none of them above the fold. An admin's first question is
 * "does anything need me", and a page that answers it four times over has not
 * answered it.
 */
function attentionItems(summary: StorageSummary): AttentionItem[] {
  const items: AttentionItem[] = [];

  // Transfer first: it is the metric that actually approaches a limit in
  // practice, and the only one on this surface that can refuse a colleague.
  for (const name of WINDOW_ORDER) {
    const window = summary.transfer[name];
    if (window.state === 'ok') continue;

    const label = STORAGE_COPY.transfer.windows[name];
    const remaining = Math.max(window.allowanceBytes - window.consumedBytes, 0);
    const fraction = window.allowanceBytes === 0 ? 1 : window.consumedBytes / window.allowanceBytes;

    items.push({
      id: name,
      text:
        window.state === 'exceeded'
          ? STORAGE_COPY.attention.transferExceeded(label)
          : STORAGE_COPY.attention.transfer(label, percentOf(fraction), bytes(remaining)),
      details: [],
    });
  }

  if (summary.storage.state !== 'ok') {
    const held = heldBytes(summary);
    const allowance = summary.storage.allowanceBytes;
    const fraction = allowance === 0 ? 1 : held / allowance;

    items.push({
      id: 'storage',
      text:
        summary.storage.state === 'exceeded'
          ? STORAGE_COPY.attention.storageExceeded
          : STORAGE_COPY.attention.storage(
              percentOf(fraction),
              bytes(Math.max(allowance - held, 0)),
            ),
      // PLN-11 travels with the figure it qualifies. Without it an admin reads
      // an over-allowance storage number as an outage, and it is a billing
      // conversation — uploads are never refused for storage.
      details: [
        summary.storage.state === 'exceeded'
          ? STORAGE_COPY.storage.exceeded
          : STORAGE_COPY.storage.warning,
      ],
    });
  }

  // PLN-6 puts the Evidence upgrade signal on this dashboard rather than leaving
  // it in the log, and it is the one item here that is an opportunity rather
  // than a problem.
  if (summary.rejectedForSize.count > 0) {
    items.push({
      id: 'rejected',
      text: STORAGE_COPY.attention.rejected(summary.rejectedForSize.count),
      details: summary.rejectedForSize.truncated
        ? [STORAGE_COPY.rejected.hint, STORAGE_COPY.rejected.truncated]
        : [STORAGE_COPY.rejected.hint],
    });
  }

  return items;
}

/**
 * **Absent when nothing warrants it**, which is the whole point — a standing
 * "all clear" is the equal-weight problem in a different costume, and a banner
 * that is always there is one nobody reads on the day it changes.
 *
 * `role="status"` and never `alert`: nothing has failed. Being at 85% of a
 * transfer allowance is a designed outcome the surface exists to report.
 *
 * **`bp-warning`, not amber.** The design system reserves amber for time and for
 * "active", and this is neither — `AppShell`'s own banner made exactly this call
 * for exactly this reason. The tint recipe is `Badge`'s, so the callout sits in
 * the same family as every other tinted surface here.
 */
function NeedsAttention({ summary }: { summary: StorageSummary }) {
  const items = attentionItems(summary);

  if (items.length === 0) return null;

  return (
    <div
      role="status"
      className="flex flex-col gap-2 rounded-md border p-3"
      style={{
        borderColor: `color-mix(in srgb, ${token('bp-warning')} 35%, transparent)`,
        background: `color-mix(in srgb, ${token('bp-warning')} 12%, transparent)`,
      }}
    >
      <span
        className="text-2xs font-semibold uppercase tracking-label"
        style={{ color: token('bp-warning') }}
      >
        {STORAGE_COPY.attention.heading}
      </span>

      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <li key={item.id} className="flex flex-col gap-0.5">
            <span className="flex items-center gap-2 text-sm text-body">
              <StatusDot tone="amber" size="sm" />
              {item.text}
            </span>
            {item.details.map((detail) => (
              <span key={detail} className="pl-4 text-xs text-muted">
                {detail}
              </span>
            ))}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The four numbers this page exists to state, and the bar that shows how the
 * first decomposes into the next three.
 *
 * `used` and `available` are the same measurement from opposite ends, and both
 * earn a tile: "19.7 GB of 1 TB" answers *how much have we spent*, "980.3 GB
 * available" answers *how much room is left*. An admin deciding whether to keep
 * uploading is asking the second, and the old surface made them subtract a
 * terabyte in their head to get it.
 */
function Capacity({ summary }: { summary: StorageSummary }) {
  const { storage, holdings } = summary;
  const held = heldBytes(summary);
  const allowance = storage.allowanceBytes;
  const fraction = allowance === 0 ? 1 : held / allowance;
  const percent = percentOf(fraction);
  const remaining = Math.max(allowance - held, 0);

  return (
    <section className="flex flex-col gap-3">
      <Eyebrow as="h2">{STORAGE_COPY.summary.heading}</Eyebrow>

      <ul
        aria-label={STORAGE_COPY.summary.tilesLabel}
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
      >
        <StatTile
          label={STORAGE_COPY.summary.used.label}
          value={bytes(held)}
          detail={STORAGE_COPY.summary.used.of(bytes(allowance))}
        />
        <StatTile
          label={STORAGE_COPY.summary.active.label}
          value={bytes(storage.bytes)}
          detail={STORAGE_COPY.summary.files(storage.fileCount)}
        />
        <StatTile
          label={STORAGE_COPY.summary.recycleBin.label}
          value={bytes(holdings.recycleBin.bytes)}
          detail={STORAGE_COPY.summary.files(holdings.recycleBin.fileCount)}
        />
        <StatTile
          label={STORAGE_COPY.summary.available.label}
          value={bytes(remaining)}
          detail={STORAGE_COPY.summary.available.free(100 - percent)}
        />
      </ul>

      <CapacityBar
        activeBytes={storage.bytes}
        binBytes={holdings.recycleBin.bytes}
        allowanceBytes={allowance}
        percent={percent}
      />
    </section>
  );
}

/**
 * One tile. `CARD_SURFACE_CLASS` is `CardGrid`'s, exported precisely for
 * card-shaped things that are not `NavCard` spaces — the grid itself is not
 * reused, because it is three-up and this row is four.
 *
 * Value over label rather than under it: the number is what a reader is scanning
 * for, and `text-h2` is the largest size on the page that does not compete with
 * the surface's own `text-h1` title.
 */
function StatTile({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <li className={CARD_SURFACE_CLASS}>
      <Eyebrow>{label}</Eyebrow>
      <span className="text-h2 font-semibold tracking-tight text-body">{value}</span>
      <span className="text-xs text-muted">{detail}</span>
    </li>
  );
}

/**
 * The allowance, split into what is active, what is binned, and what is free.
 *
 * **One `progressbar`, three fills.** `aria-valuenow` reports the overall
 * percentage held and the segments are `aria-hidden`: a progressbar carries
 * exactly one value, and three stacked widths cannot honestly be that one
 * number. The tiles above already state the split as text, so a screen reader
 * loses nothing to the hiding.
 *
 * **The denominator is `max(allowance, held)`, not the allowance.** Storage over
 * its allowance is a designed state (PLN-11 never refuses an upload), so the
 * segments have to keep summing to the full track rather than overflowing it —
 * an over-allowance agency sees a full bar, and the callout says why.
 *
 * Two intensities of one hue rather than blue-and-amber: the split is a
 * composition, not a warning, and warm colour on this surface now means
 * something needs attention.
 */
function CapacityBar({
  activeBytes,
  binBytes,
  allowanceBytes,
  percent,
}: {
  activeBytes: number;
  binBytes: number;
  allowanceBytes: number;
  percent: number;
}) {
  const scale = Math.max(allowanceBytes, activeBytes + binBytes);
  const width = (value: number) => (scale === 0 ? 0 : (value / scale) * 100);

  return (
    <div className="flex flex-col gap-2">
      <div
        role="progressbar"
        aria-label={STORAGE_COPY.summary.meterLabel}
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        className="flex h-2 w-full overflow-hidden"
        style={{ background: token('bp-panel'), borderRadius: token('radius-full') }}
      >
        <div
          aria-hidden="true"
          className="h-full transition-all"
          style={{ width: `${String(width(activeBytes))}%`, background: token('bp-blue') }}
        />
        <div
          aria-hidden="true"
          className="h-full transition-all"
          style={{ width: `${String(width(binBytes))}%`, background: token('bp-blue-rail') }}
        />
      </div>

      <ul
        aria-label={STORAGE_COPY.summary.legendLabel}
        className="flex flex-wrap gap-4 text-xs text-muted"
      >
        <LegendSwatch color={token('bp-blue')} label={STORAGE_COPY.summary.legend.active} />
        <LegendSwatch
          color={token('bp-blue-rail')}
          label={STORAGE_COPY.summary.legend.recycleBin}
        />
        <LegendSwatch color={token('bp-panel')} label={STORAGE_COPY.summary.legend.free} />
      </ul>
    </div>
  );
}

/** The bar carries no labels of its own, so the key has to name each fill. */
function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <span
        aria-hidden="true"
        className="inline-block size-2 shrink-0 rounded-full"
        style={{ background: color }}
      />
      {label}
    </li>
  );
}

/**
 * STO-3's second line, which that requirement's own words call "the more
 * important one" — promoted out of the footnote list it shared with the bin.
 *
 * The two are opposite kinds of holding and no longer belong together. The bin
 * is **reclaimable**, so it is a tile in the summary above where somebody might
 * act on it. This is storage the agency has committed to non-cancellably
 * (RET-8), so it leads with that consequence rather than with a byte count.
 *
 * **Absent for a Docs agency, rather than zero** (RET-5). `holdings.retention`
 * being `undefined` means the agency cannot buy the product this reports, so
 * there is nothing to measure; an Evidence agency retaining nothing *does* get a
 * zero, because that is a number somebody can move. The asymmetry is the point.
 */
function RetainedStorage({ holdings }: { holdings: StorageSummary['holdings'] }) {
  const { retention } = holdings;

  if (retention === undefined) return null;

  return (
    <section className="flex flex-col gap-2">
      <Eyebrow as="h2">{STORAGE_COPY.retention.heading}</Eyebrow>

      {retention.fileCount === 0 ? (
        <p className="text-sm text-muted">{STORAGE_COPY.retention.none}</p>
      ) : (
        <>
          <p className="text-sm text-body">
            {STORAGE_COPY.retention.locked(
              bytes(retention.bytes),
              STORAGE_COPY.summary.files(retention.fileCount),
            )}
          </p>

          {retention.byClass.length === 0 ? null : (
            <ul className="flex flex-col gap-1">
              {retention.byClass.map((row) => (
                <li
                  key={row.retentionClass}
                  className="flex flex-wrap items-baseline gap-2 text-xs"
                >
                  <span className="text-body">
                    {STORAGE_COPY.retention.classes[row.retentionClass]}
                  </span>
                  <span className="text-muted">
                    {STORAGE_COPY.retention.row(
                      STORAGE_COPY.summary.files(row.fileCount),
                      bytes(row.bytes),
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* Both halves are SPEC, and neither is droppable: *already counted*
              is STO-3's amendment, which stops a reader summing this section
              into the one above; *non-cancellable* is RET-8, and is what makes
              this the more important of the two holdings. */}
          <p className="text-xs text-muted">{STORAGE_COPY.retention.note}</p>
        </>
      )}
    </section>
  );
}

/** One row of the transfer table — PLN-10's three windows. */
interface TransferRow {
  id: (typeof WINDOW_ORDER)[number];
  label: string;
  since: string | undefined;
  consumedBytes: number;
  allowanceBytes: number;
  percent: number;
  state: StorageSummary['storage']['state'];
}

/**
 * PLN-10's three windows as a table rather than three stacked meters.
 *
 * **The per-window badges are gone and the policy is stated once.** Each window
 * used to carry a chip — `WOULD THROTTLE` on the two rolling ones, `INVOICED,
 * NEVER THROTTLED` on the annual — above a footer explaining PLN-12's staging.
 * Three chips saying two different kinds of thing, and the pair read as a
 * contradiction: one said a throttle would happen, the next said it never would,
 * and the footer said neither was switched on. The two facts are unchanged and
 * both still here; they are now one sentence each, below the numbers they
 * qualify.
 *
 * STO-6 requires the second of those sentences while PLN-12 keeps caps soft —
 * "the fact that a cap *would* have applied" is demoted here, not dropped. And
 * `enforcing` is still read off the wire per window, never recomputed: which
 * windows bite is a rule, and a rule in two places drifts.
 */
function TransferAllowance({ summary }: { summary: StorageSummary }) {
  const rows: TransferRow[] = WINDOW_ORDER.map((name) => {
    const window = summary.transfer[name];
    const fraction = window.allowanceBytes === 0 ? 1 : window.consumedBytes / window.allowanceBytes;

    return {
      id: name,
      label: STORAGE_COPY.transfer.windows[name],
      // STO-1: the annual window is term-to-date, not the calendar year, and it
      // is the only one whose name does not already say what it covers.
      since:
        name === 'annual'
          ? STORAGE_COPY.transfer.since(formatUtcDate(summary.since, STORAGE_COPY.dateLocale))
          : undefined,
      consumedBytes: window.consumedBytes,
      allowanceBytes: window.allowanceBytes,
      percent: percentOf(fraction),
      state: window.state,
    };
  });

  const columns: DataTableColumn<TransferRow>[] = [
    {
      id: 'period',
      header: STORAGE_COPY.transfer.columns.period,
      cell: (row) => (
        <span className="flex flex-wrap items-baseline gap-2">
          <span className="text-body">{row.label}</span>
          {row.since === undefined ? null : <span className="text-muted">{row.since}</span>}
        </span>
      ),
    },
    {
      id: 'used',
      header: STORAGE_COPY.transfer.columns.used,
      align: 'end',
      cell: (row) => bytes(row.consumedBytes),
    },
    {
      id: 'allowance',
      header: STORAGE_COPY.transfer.columns.allowance,
      align: 'end',
      cell: (row) => bytes(row.allowanceBytes),
    },
    {
      id: 'status',
      header: STORAGE_COPY.transfer.columns.status,
      align: 'end',
      cell: (row) => (
        <span className="flex flex-wrap items-center justify-end gap-2">
          {STORAGE_COPY.transfer.percentUsed(row.percent)}
          {row.state === 'ok' ? null : (
            <Badge tone="amber">
              {row.state === 'exceeded' ? STORAGE_COPY.transfer.over : STORAGE_COPY.transfer.near}
            </Badge>
          )}
        </span>
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-2">
      <Eyebrow as="h2">{STORAGE_COPY.transfer.heading}</Eyebrow>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        caption={STORAGE_COPY.transfer.caption}
      />

      <div className="flex flex-col gap-1 text-xs text-muted">
        {/* ADR-0016: a cached total with no as-of is a measurement nobody took. */}
        <span>
          {summary.transferAsOf === null
            ? STORAGE_COPY.transfer.notCounted
            : STORAGE_COPY.transfer.asOf(
                formatUtcDate(summary.transferAsOf, STORAGE_COPY.dateLocale),
              )}
        </span>
        <span>{STORAGE_COPY.transfer.policy}</span>
        <span>
          {summary.enforcement === 'soft'
            ? STORAGE_COPY.transfer.soft
            : STORAGE_COPY.transfer.throttling}
        </span>
      </div>
    </section>
  );
}

/**
 * STO-2's table.
 *
 * **Named for the counterpart, not the space.** A row *is* a space, but "by space"
 * describes the storage rather than the relationship, and an agency may hold
 * several spaces (PLN-8's per-space cap exists for exactly that) — so "by agency"
 * would claim a uniqueness the model does not have. Every space has exactly one
 * counterpart, which is what an admin is actually looking at.
 *
 * **The headers name the actor, so the explanatory note could go.** PLN-8 and
 * PLN-9 attribute in opposite directions — stored bytes to whoever uploaded
 * them, read bytes to whoever read them — and a counterpart heavy in one and
 * light in the other is the normal shape rather than an anomaly. A sentence
 * above the table used to say so; `Stored by you` and `Downloaded by
 * counterpart` say it in the place a reader is already looking.
 *
 * **Sorting stays off**: the server orders it heaviest first and owns that
 * ordering, the same call `FolderView` and the Audit log make.
 */
function Breakdown({ spaces }: { spaces: StorageSpaceRow[] }) {
  const columns: DataTableColumn<StorageSpaceRow>[] = [
    {
      id: 'space',
      header: STORAGE_COPY.breakdown.columns.space,
      // The room's own name (CON-22). It was the counterpart's, and a room
      // holding six agencies has no counterpart to be named after.
      cell: (row) => (
        <span className="flex flex-wrap items-center gap-2">
          {row.spaceName}
          {row.status === 'Removed' ? (
            <Badge tone="alert">{STORAGE_COPY.breakdown.removed}</Badge>
          ) : null}
          {row.status === 'Invited' ? (
            <Badge tone="neutral">{STORAGE_COPY.breakdown.invited}</Badge>
          ) : null}
        </span>
      ),
    },
    {
      id: 'files',
      header: STORAGE_COPY.breakdown.columns.files,
      align: 'end',
      cell: (row) => String(row.fileCount),
    },
    {
      id: 'stored',
      header: STORAGE_COPY.breakdown.columns.stored,
      align: 'end',
      cell: (row) => bytes(row.bytes),
    },
    {
      id: 'share',
      header: STORAGE_COPY.breakdown.columns.share,
      align: 'end',
      cell: (row) => `${(row.shareOfTotal * 100).toFixed(1)}%`,
    },
    {
      id: 'transfer',
      header: STORAGE_COPY.breakdown.columns.transfer,
      align: 'end',
      cell: (row) => bytes(row.transferBytes),
    },
    {
      id: 'recipientTransfer',
      header: STORAGE_COPY.breakdown.columns.recipientTransfer,
      align: 'end',
      cell: (row) => bytes(row.recipientTransferBytes),
    },
  ];

  return (
    <section className="flex flex-col gap-2">
      <Eyebrow as="h2">{STORAGE_COPY.breakdown.heading}</Eyebrow>

      <DataTable
        columns={columns}
        rows={spaces}
        rowKey={(row) => row.spaceId}
        caption={STORAGE_COPY.breakdown.caption}
        emptyState={STORAGE_COPY.breakdown.empty}
      />

      {/* The one thing the headers cannot carry: `shareOfTotal`'s denominator is
          every held byte, active and binned, while `Stored by you` beside it
          counts active bytes only. Without this the two columns look as though
          they should divide into each other. */}
      <p className="text-xs text-muted">{STORAGE_COPY.breakdown.shareNote}</p>

      {spaces.some((row) => row.status === 'Removed') ? (
        <p className="text-xs text-muted">{STORAGE_COPY.breakdown.removedNote}</p>
      ) : null}
    </section>
  );
}

/**
 * The refusals.
 *
 * **A 403 has two causes here and they need different sentences**, unlike the
 * Audit surface where it has one. A member is refused because the surface is
 * admin-only (PERM-5); a `Receiving` or `Provisioning` agency is refused because
 * it has no plan to report against (STO-5, CON-17), and the API's own message
 * names which and what to do about it. So this reaches for `messageFrom` rather
 * than printing a fixed line — the same call `AddAgencyPanel` and `MembersTab`
 * make, and for the same reason.
 *
 * There is no 404 branch: the route takes no id, so nothing about it can be
 * absent.
 */
function SummaryError({
  status,
  error,
  onRetry,
}: {
  status: number;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-col items-start gap-3">
      <p role="alert" className="flex items-center gap-2 text-sm text-danger">
        <StatusDot tone="alert" />
        {messageFrom(error, STORAGE_COPY.states.error)}
      </p>

      {status === 403 ? null : (
        <button
          type="button"
          onClick={onRetry}
          className="w-fit border px-3 py-1.5 text-xs font-medium transition-colors"
          style={{
            borderColor: token('bp-border-strong'),
            color: token('bp-text'),
            borderRadius: token('radius-md'),
          }}
        >
          {STORAGE_COPY.states.retry}
        </button>
      )}
    </div>
  );
}
